import {
    DELIVERY_CUTOFF_HOUR,
    DELIVERY_CUTOFF_MINUTE,
    DELIVERY_TIMEZONE,
    excludeDates,
    weekdayNames,
    weekdayShort,
    PaymentMethods,
    TransportInforms,
    gallonsForFreeDelivery,
    orderAmountForFreeDelivery,
} from '~/constants/delivery'
import { resolveCityProfile } from '~/constants/cityDelivery'
import type { DeliveryClockInfo, DeliveryDayOption, PaymentMethodOption, TransportInformOption } from '~/types/delivery'

export interface DeliveryInfo {
    freeDelivery: boolean
    gallonsToFreeDelivery: number
    amountToFreeDelivery: number
    amountProgressPercent: number
}

export function computeDeliveryInfo(gallonQty: number, totalPrice: number): DeliveryInfo {
    const gallonsToFreeDelivery = Math.max(0, gallonsForFreeDelivery - gallonQty)
    const amountToFreeDelivery = Math.max(0, orderAmountForFreeDelivery - totalPrice)
    return {
        gallonsToFreeDelivery,
        amountToFreeDelivery,
        amountProgressPercent: Math.min(100, Math.round((totalPrice / orderAmountForFreeDelivery) * 100)),
        freeDelivery: gallonQty >= gallonsForFreeDelivery || totalPrice >= orderAmountForFreeDelivery,
    }
}

interface ZoneDateTime {
    year: number
    month: number
    day: number
    hour: number
    minute: number
}

function getDateTimeInZone(now: Date, timeZone: string): ZoneDateTime {
    const parts = new Intl.DateTimeFormat('en-US', {
        timeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
    }).formatToParts(now)

    const get = (type: Intl.DateTimeFormatPartTypes) =>
        Number(parts.find(p => p.type === type)?.value ?? 0)

    return {
        year: get('year'),
        month: get('month'),
        day: get('day'),
        hour: get('hour'),
        minute: get('minute'),
    }
}

function formatTime({ hour, minute }: Pick<ZoneDateTime, 'hour' | 'minute'>): string {
    return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`
}

function isCutoffPassed({ hour, minute }: Pick<ZoneDateTime, 'hour' | 'minute'>): boolean {
    return hour > DELIVERY_CUTOFF_HOUR
        || (hour === DELIVERY_CUTOFF_HOUR && minute >= DELIVERY_CUTOFF_MINUTE)
}

function getBusinessDateTime(now: Date = new Date()): ZoneDateTime {
    return getDateTimeInZone(now, DELIVERY_TIMEZONE)
}

function toIsoDate({ year, month, day }: Pick<ZoneDateTime, 'year' | 'month' | 'day'>): string {
    return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
}

function isoToDate(iso: string): Date {
    const [y, m, d] = iso.split('-').map(Number)
    return new Date(y!, m! - 1, d!)
}

function addCalendarDays(iso: string, days: number): string {
    const date = isoToDate(iso)
    date.setDate(date.getDate() + days)
    return toIsoDate({ year: date.getFullYear(), month: date.getMonth() + 1, day: date.getDate() })
}

function isWorkingDay(iso: string, excludeSet: Set<string>): boolean {
    const dayOfWeek = isoToDate(iso).getDay()
    return dayOfWeek !== 0 && dayOfWeek !== 6 && !excludeSet.has(iso)
}

function nextWorkingDay(fromIso: string, excludeSet: Set<string>): string {
    let cursor = addCalendarDays(fromIso, 1)
    while (!isWorkingDay(cursor, excludeSet)) {
        cursor = addCalendarDays(cursor, 1)
    }
    return cursor
}

function collectWorkingDays(startIso: string, excludeSet: Set<string>, count: number): string[] {
    const results: string[] = []
    let cursor = startIso
    while (results.length < count) {
        if (isWorkingDay(cursor, excludeSet)) {
            results.push(cursor)
        }
        cursor = addCalendarDays(cursor, 1)
    }
    return results
}

function formatDateLabel(iso: string, locale: string): string {
    return isoToDate(iso).toLocaleDateString(locale, { day: 'numeric', month: 'long' })
}

function formatDateFull(iso: string, locale: string): string {
    const [year, month, day] = iso.split('-')
    const numeric = `${day}.${month}.${year}`
    return locale.startsWith('bg') ? `${numeric} г.` : numeric
}

/** Order summary date, e.g. „чт, 22.10.2026“ / „Thu, 22.10.2026“. */
export function formatOrderSummaryDate(iso: string, locale: string): string {
    const names = weekdayShort[locale] ?? weekdayShort.bg ?? []
    const [year, month, day] = iso.split('-')
    const weekday = names[isoToDate(iso).getDay()] ?? ''
    return `${weekday}, ${day}.${month}.${year}`
}

export function getDeliveryClockInfo(now: Date = new Date()): DeliveryClockInfo {
    const business = getBusinessDateTime(now)
    const deviceTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone
    const device = getDateTimeInZone(now, deviceTimezone)

    return {
        businessTimezone: DELIVERY_TIMEZONE,
        deviceTimezone,
        businessTime: formatTime(business),
        deviceTime: formatTime(device),
        businessDate: toIsoDate(business),
        cutoffPassed: isCutoffPassed(business),
        cutoffAppliesIn: DELIVERY_TIMEZONE,
    }
}
export function isSofiaCity(cityName: string): boolean {
    return resolveCityProfile(cityName) === 'sofia'
}

export interface DeliveryDayQuery {
    skipEarliest?: boolean
    count?: number
    now?: Date
}

export function getDeliveryDayOptions(
    t: (key: string) => string,
    locale: string = 'bg',
    query: DeliveryDayQuery = {},
): DeliveryDayOption[] {
    const count = query.count ?? 4
    const skipEarliest = query.skipEarliest ?? false
    const excludeSet = new Set(excludeDates)
    const business = getBusinessDateTime(query.now)
    const todayIso = toIsoDate(business)
    const tomorrowIso = addCalendarDays(todayIso, 1)

    const isWorkingToday = isWorkingDay(todayIso, excludeSet)
    const cutoffPassed = isCutoffPassed(business)

    const firstIso = (isWorkingToday && !cutoffPassed)
        ? todayIso
        : nextWorkingDay(todayIso, excludeSet)

    const collected = collectWorkingDays(firstIso, excludeSet, skipEarliest ? count + 1 : count)
    const workingDays = skipEarliest ? collected.slice(1) : collected
    const names: string[] = weekdayNames[locale] ?? weekdayNames['bg'] ?? []

    return workingDays.map((iso, index) => {
        let label: string
        if (iso === todayIso) {
            label = t('order.today')
        } else if (iso === tomorrowIso) {
            label = t('order.tomorrow')
        } else {
            const dayOfWeek = isoToDate(iso).getDay()
            label = names[dayOfWeek] || isoToDate(iso).toLocaleDateString(locale, { weekday: 'long' })
        }

        return {
            label,
            note: !skipEarliest && index === 0 ? t('order.ifPossible') : undefined,
            date: iso,
            dateFormatted: formatDateFull(iso, locale),
            dateLabel: formatDateLabel(iso, locale),
            key: `date-${iso}`,
        }
    })
}

/** Lowercase day label for use inside a sentence (e.g. „утре“, „в четвъртък“, „on Thursday“). */
export function formatDayLabelInSentence(
    label: string,
    locale: string,
    t: (key: string) => string,
    options: { preposition?: boolean } = {},
): string {
    const { preposition = true } = options
    const localeTag = locale || 'bg'
    const lower = label.toLocaleLowerCase(localeTag)
    const today = t('order.today').toLocaleLowerCase(localeTag)
    const tomorrow = t('order.tomorrow').toLocaleLowerCase(localeTag)

    if (lower === today || lower === tomorrow || !preposition) {
        return lower
    }

    if (localeTag.startsWith('bg')) {
        return `в ${lower}`
    }

    return `on ${lower}`
}

export function getPaymentMethodOptions(
    t: (key: string) => string,
    options: { showCashOnDelivery?: boolean } = {},
): PaymentMethodOption[] {
    const showCash = options.showCashOnDelivery ?? true
    return Object.values(PaymentMethods)
        .filter(option => showCash
            ? option.value !== PaymentMethods.cod.value
            : option.value !== PaymentMethods.cash.value)
        .map(option => ({
            label: t(option.label),
            value: option.value,
        }))
}

export function getTransformInformOptions(t: (key: string) => string): TransportInformOption[] {
    return Object.values(TransportInforms).map(option => ({
        label: t(option.label),
        value: option.value,
    })).slice(1);
}
